import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useGetClaimEdit } from './useGetClaimEdit';
import { useZipcode } from './useZipcodeQuery';
import { useOptionZipcode } from './useOptionZipcode';
import { UpdateClaimEdit } from '../../../../resources/payload';
import { onUploadFile } from './onUploadFile';
import { breakdownSubsQuery } from '../../../../services/react-query/optionMasterQuery';

export const useGetClaimEditList = () => {
  const { control, getValues, setValue, reset, watch, handleSubmit } = useForm();
  const [timelineList, setTimelineList] = useState();
  const [claimData, setClaimData] = useState();
  const [claimEditData, setClaimEditData] = useState();
  const [state, setState] = useState();
  const [breakdownList, setBreakdownList] = useState();

  const queryParams = new URLSearchParams(window.location.search);
  const claimCode = queryParams.get('code');
  const productType = watch('productTypeCode');
  const breakdownId = watch('breakdownId');

  const { mutate: onCallZipCode, loading: isLoadingZipCode } = useZipcode({ reset, getValues });
  const { mutate: onEditClaim, loading: isloadingToEdit } = onUploadFile();
  const { provinceOptions, districtOptions, subDistrictOptions } = useOptionZipcode({
    watch,
  });

  const { data: breakdownSubsAll, isLoading: isLoadingBreakdownSubs } = breakdownSubsQuery({
    productType,
  });

  // API กรองมาแค่ระดับ productType ต้องกรองด้วยอาการเสียหลักอีกชั้น
  // ไม่งั้นเลือกย่อยข้ามอาการหลักได้ แล้วไปโดน backend ปฏิเสธตอนกดบันทึก
  const breakdownSubsOptions = useMemo(() => {
    const list = (breakdownSubsAll ?? []).filter((item) => item.breakdownId === breakdownId);
    // ตอนเปิดหน้ามา options ยังโหลดไม่เสร็จ antd Select จะโชว์ค่าเป็นเลข id
    // เติมค่าเดิมของเคลมเข้าไปเองถ้ายังไม่มีในลิสต์ (เฉพาะตอนที่ยังไม่เปลี่ยนอาการหลัก)
    const currentSubId = claimData?.breakdownSubId;
    const isOriginalBreakdown = breakdownId === claimData?.breakdownId;
    if (isOriginalBreakdown && currentSubId && !list.some((item) => item.value === currentSubId)) {
      return [
        {
          label: claimData?.breakdownSubNameTh || `${currentSubId}`,
          value: currentSubId,
          breakdownId,
        },
        ...list,
      ];
    }
    return list;
  }, [breakdownSubsAll, breakdownId, claimData]);

  // เปลี่ยนอาการเสียหลักแล้วอาการย่อยเดิมใช้ไม่ได้อีก ต้องล้างให้เลือกใหม่
  const onChangeClaimInfo = (_value, item) => {
    if (item?.name === 'breakdownId') {
      setValue('breakdownSubId', '');
    }
  };

  const onEnterZipcode = (value) => {
    onCallZipCode(value, {
      onSuccess: (result) => {
        reset({
          ...getValues(),
          ...result,
          provinceId: '',
          districtId: '',
          subDistrictId: '',
          other: '',
        });
      },
    });
  };

  const { dataSource, loading: LoadingClaimEdit } = useGetClaimEdit({
    claimCode: state?.claimCode,
    reset,
    setValue,
    getValues,
  });
  useEffect(() => {
    if (claimCode) {
      setState({ claimCode });
    }
    if (dataSource) {
      setClaimEditData(dataSource);
    }
    if (claimEditData) {
      setClaimData(claimEditData?.claim);
      if (claimData?.remainingDays > 300000) {
        setValue('remainingDays', 'ตลอดชีวิต');
      }
      updateTimeline(claimEditData);
    }

    const autoCallZipCode = (values) => {
      if (values) {
        onCallZipCode(values);
      }
    };

    autoCallZipCode(claimEditData?.claim?.zipCode);
    const breakdown =
      claimEditData?.breakdowns?.map((item) => ({
        label: item?.breakdownNameTh,
        value: item?.breakdownId,
      })) ?? [];

    // อาการเสียหลักเดิมของเคลมอาจไม่อยู่ในลิสต์ที่ API คืนมา (ถูกปิดใช้งานไปแล้ว)
    // ถ้าไม่เติมเข้าไป Select จะโชว์เป็นเลข id
    const currentBreakdownId = claimEditData?.claim?.breakdownId;
    if (currentBreakdownId && !breakdown.some((item) => item.value === currentBreakdownId)) {
      breakdown.unshift({
        label: claimEditData?.claim?.breakdownName || `${currentBreakdownId}`,
        value: currentBreakdownId,
      });
    }

    setBreakdownList(breakdown);
  }, [claimCode, dataSource, claimEditData, setValue, claimData]);

  const updateTimeline = () => {
    if (claimEditData?.timelineList) {
      const timeline = claimEditData.timelineList.map((item) => ({
        text: item?.optionStatusNameTh,
        color: item?.optionStatusCode === claimEditData?.claim.claimStatus ? 'green' : 'black',
      }));
      setTimelineList(timeline);
    }
  };

  const onSubmit = () =>
    handleSubmit((values) => {
      const formData = UpdateClaimEdit(values);
      onEditClaim(formData);
    })();

  return {
    control,
    getValues,
    setValue,
    timelineList,
    LoadingClaimEdit,
    claimData,
    onEnterZipcode,
    provinceOptions,
    districtOptions,
    subDistrictOptions,
    breakdownList,
    onSubmit,
    handleSubmit,
    isloadingToEdit,
    isLoadingZipCode,
    breakdownSubsOptions,
    isLoadingBreakdownSubs,
    onChangeClaimInfo,
  };
};
